import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { CarPositionNode } from '@/apollo/graphql'
import {
  AlertModal,
  AppLink,
  Button,
  DateTimePicker,
  DropDownPicker,
} from '@/components'
import { useAPI } from '@/hooks/index'
import { t } from '@/i18n'
import { useSelect as useCustomersSelect } from '@/pages/Customer/useSelect'
import { useSelect as useDealsSelect } from '@/pages/Deals/onSelect'
import { useSelect as useDriversSelect } from '@/pages/Driver/useSelect'
import {
  addDriverPath,
  customerDisplayLabel,
  driverDisplayLabel,
  editCustomerPath,
  editDealPath,
  editDriverPath,
  generatePath,
} from '@/utils'

interface Props {
  carId: string
  data: Partial<CarPositionNode>
  refetch: () => void
}

const HistoryItem: React.FC<Props> = ({ carId, data, refetch }) => {
  const { submitting, sendForm } = useAPI()
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const [editData, setEditData] = useState<Partial<CarPositionNode>>(data)
  const [editing, setEditing] = useState<boolean>(false)

  // Add react-hook-form control
  const { control } = useForm({
    defaultValues: {
      driver: editData.driver?.id || '',
      customer: editData.customer?.id || '',
      deal: editData.deal?.id || '',
    },
  })

  useEffect(() => {
    setEditData(data)
  }, [data])

  const {
    driverOptions,
    pageInfo: driversPageInfo,
    loadingDrivers,
    onFetchMore: onFetchMoreDrivers,
    onSearch: onSearchDrivers,
  } = useDriversSelect({
    loading: false,
    initial: data.driver,
  })

  const {
    customerOptions,
    pageInfo: customersPageInfo,
    loading: loadingCustomers,
    onFetchMore: onFetchMoreCustomers,
    onSearch: onSearchCustomers,
  } = useCustomersSelect({
    initial: data.customer,
  })

  const {
    data: dealsData,
    pageInfo: dealsPageInfo,
    loading: loadingDeals,
    onFetchMore: onFetchMoreDeals,
    onSearch: onSearchDeals,
  } = useDealsSelect()

  // Format deal options
  const dealOptions = useMemo(() => {
    return dealsData.map(deal => ({
      label: `Deal ${deal.id}`,
      value: deal.id || '',
    }))
  }, [dealsData])

  const onCancelEdit = useCallback(() => {
    setEditData(data)
    setEditing(false)
  }, [data])

  const onSave = useCallback(() => {
    const d = {
      start_time: editData.startTime,
      end_time: editData.endTime,
      car: carId,
      driver: editData.driver?.id,
      customer: editData.customer?.id,
      deal: editData.deal?.id,
    }
    sendForm(
      {
        path: `cars/car-position/${data.id}/`,
        method: 'PUT',
        data: d,
        callback: () => {
          setEditing(false)
          refetch()
        },
      },
      { showNotify: true },
    )
  }, [
    editData.startTime,
    editData.endTime,
    editData.driver?.id,
    editData.customer?.id,
    editData.deal?.id,
    carId,
    sendForm,
    data.id,
    refetch,
  ])

  const onChange = useCallback((name: string, value: any) => {
    if (name === 'driver') {
      // @ts-ignore
      setEditData(prev => ({
        ...prev,
        driver: { ...(prev.driver || {}), id: value },
      }))
    } else if (name === 'customer') {
      setEditData(prev => ({
        ...prev,
        customer: { ...(prev.customer || {}), id: value } as any,
      }))
    } else if (name === 'deal') {
      setEditData(prev => ({
        ...prev,
        deal: { ...(prev.deal || {}), id: value } as any,
      }))
    } else if (name === 'startTime') {
      setEditData(prev => ({
        ...prev,
        startTime: value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : null,
      }))
    } else if (name === 'endTime') {
      setEditData(prev => ({
        ...prev,
        endTime: value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : null,
      }))
    }
  }, [])

  const onDelete = useCallback(() => {
    sendForm(
      {
        path: `cars/car-position/${data.id}/`,
        method: 'DELETE',
        data: {},
        callback: () => {
          setShowDeleteModal(false)
          refetch()
        },
      },
      { showNotify: true },
    )
  }, [sendForm, data.id, refetch])

  return (
    <>
      <tr>
        <td>
          {editing ? (
            <DateTimePicker
              value={editData.startTime}
              showTimeSelect
              timeIntervals={1}
              dateFormat="dd/MM/yyyy HH:mm"
              onChange={(v: any) => onChange('startTime', v)}
            />
          ) : editData.startTime ? (
            moment(editData.startTime).format('ll HH:mm:ss')
          ) : (
            '-'
          )}
        </td>
        <td>
          {editing ? (
            <DateTimePicker
              value={editData.endTime}
              showTimeSelect
              timeIntervals={1}
              dateFormat="dd/MM/yyyy HH:mm"
              onChange={(v: any) => onChange('endTime', v)}
            />
          ) : editData.endTime ? (
            moment(editData.endTime).format('ll HH:mm:ss')
          ) : (
            '-'
          )}
        </td>
        <td>
          {editing ? (
            <DropDownPicker
              isSearchable
              isAsync
              name="driver"
              control={control}
              isEditing={editing}
              options={driverOptions}
              optionsLoading={loadingDrivers}
              fieldValue={editData.driver?.id}
              emptyValue=""
              disabled={false}
              fetchMoreStatus={driversPageInfo.fetchMoreStatus}
              onFetchMore={onFetchMoreDrivers}
              onFieldChange={(v: any) => onChange('driver', v)}
              onSearch={onSearchDrivers}
            />
          ) : (
            <AppLink
              className="field-view-link justify-center"
              label={driverDisplayLabel(editData.driver)}
              link={generatePath(
                editData.driver?.id ? editDriverPath : addDriverPath,
                {
                  id: editData.driver?.id,
                  carId,
                },
              )}
              target="_blank"
            />
          )}
        </td>
        <td>{editData.location}</td>
        <td>
          {editing ? (
            <DropDownPicker
              isSearchable
              isAsync
              name="customer"
              control={control}
              isEditing={editing}
              options={customerOptions}
              optionsLoading={loadingCustomers}
              fieldValue={editData.customer?.id}
              emptyValue=""
              disabled={false}
              fetchMoreStatus={customersPageInfo.fetchMoreStatus}
              onFetchMore={onFetchMoreCustomers}
              onFieldChange={(v: any) => onChange('customer', v)}
              onSearch={onSearchCustomers}
            />
          ) : (
            editData.customer && (
              <AppLink
                className="field-view-link justify-center"
                label={customerDisplayLabel(editData.customer)}
                link={generatePath(editCustomerPath, {
                  id: editData.customer?.id,
                })}
                target="_blank"
              />
            )
          )}
        </td>
        <td>
          {editing ? (
            <DropDownPicker
              isSearchable
              isAsync
              name="deal"
              control={control}
              isEditing={editing}
              options={dealOptions}
              optionsLoading={loadingDeals}
              fieldValue={editData.deal?.id}
              emptyValue=""
              disabled={false}
              fetchMoreStatus={dealsPageInfo.fetchMoreStatus}
              onFetchMore={onFetchMoreDeals}
              onFieldChange={(v: any) => onChange('deal', v)}
              onSearch={onSearchDeals}
            />
          ) : (
            editData.deal && (
              <AppLink
                className="field-view-link justify-center"
                label={editData.deal?.id || ''}
                link={generatePath(editDealPath, {
                  id: editData.deal?.id,
                })}
                target="_blank"
              />
            )
          )}
        </td>
        <td>
          <div className="action-buttons">
            {editing ? (
              <>
                <Button
                  disabled={submitting}
                  className="main-btn info"
                  onClick={onCancelEdit}
                >
                  {t('cancel')}
                </Button>
                <Button
                  disabled={submitting}
                  className="main-btn success"
                  onClick={onSave}
                >
                  {t('save')}
                </Button>
              </>
            ) : (
              <>
                <Button className="main-btn" onClick={() => setEditing(true)}>
                  {t('edit')}
                </Button>
                <Button
                  className="main-btn danger"
                  onClick={() => setShowDeleteModal(true)}
                >
                  {t('delete')}
                </Button>
              </>
            )}
          </div>
        </td>
      </tr>
      <AlertModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onSubmit={onDelete}
        submitText={t('delete')}
        cancelText={t('cancel')}
        onCancel={() => setShowDeleteModal(false)}
      >
        {t('confirm_delete_object', { objectName: t('car_position') })}
      </AlertModal>
    </>
  )
}

export default HistoryItem
